/// <reference types="vite/client" />
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { createClient } from '@supabase/supabase-js';
import Cookies from 'js-cookie';
import { DevLoginScreen } from './DevLoginScreen.js';
// The proxy that apps will use
export let proxySupabase = null;
// The injector
export const _injectAuthenticatedClient = (client) => {
    proxySupabase = client;
};
// Module-level token store — updated after each Edge Function exchange.
// The custom fetch interceptor reads from here on every request.
let _currentAccessToken = null;
const AuthContext = createContext({
    user: null,
    loading: true,
    supabase: null,
    accessToken: null,
    allowedApps: [],
    signOut: async () => { },
});
export const AuthProvider = ({ children, firebaseConfig, supabaseUrl, supabaseAnonKey, loginRedirectUrl, allowLocalBypass = false, appId, }) => {
    var _a;
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [supabase, setSupabase] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [allowedApps, setAllowedApps] = useState([]);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [ssoFailed, setSsoFailed] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);
    const hydratingRef = useRef(true);
    useEffect(() => {
        // 1. Initialize Firebase
        const safeFirebaseConfig = {
            ...firebaseConfig,
            authDomain: firebaseConfig.authDomain,
        };
        if (!getApps().length) {
            initializeApp(safeFirebaseConfig);
        }
        const auth = getAuth();
        // 2. Initialize Supabase client.
        //    A custom fetch interceptor injects the Supabase access token on every request.
        //    This is more reliable than setSession() with persistSession: false.
        const supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
            auth: {
                persistSession: false,
                autoRefreshToken: false,
            },
            global: {
                fetch: async (url, options = {}) => {
                    const headers = {};
                    // Copy any existing headers
                    if (options.headers instanceof Headers) {
                        options.headers.forEach((v, k) => { headers[k] = v; });
                    }
                    else if (options.headers) {
                        Object.assign(headers, options.headers);
                    }
                    // Inject the authenticated token if we have one.
                    // Delete BOTH case variants to prevent a comma-separated merge
                    // (Headers objects normalize to lowercase, plain objects may be capitalized).
                    if (_currentAccessToken) {
                        delete headers['authorization'];
                        delete headers['Authorization'];
                        headers['Authorization'] = `Bearer ${_currentAccessToken}`;
                    }
                    return fetch(url, { ...options, headers });
                },
            },
        });
        setSupabase(supabaseClient);
        _injectAuthenticatedClient(supabaseClient);
        // 3. Exchange a Firebase ID token for a real Supabase session.
        //    Called on sign-in and every ~1hr token refresh, keeping Supabase session alive.
        //    The Edge Function creates an auth.users row if one doesn't exist yet.
        const exchangeForSupabaseSession = async (firebaseUser) => {
            try {
                const firebaseToken = await firebaseUser.getIdToken();
                const exchangeHeaders = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${supabaseAnonKey}`,
                };
                if (appId) {
                    exchangeHeaders['x-app-id'] = appId;
                }
                const response = await fetch(`${supabaseUrl}/functions/v1/firebase-to-supabase-session`, {
                    method: 'POST',
                    headers: exchangeHeaders,
                    body: JSON.stringify({ firebaseToken }),
                });
                if (!response.ok) {
                    // Check for app access denied
                    if (response.status === 403) {
                        try {
                            const errBody = await response.json();
                            if (errBody.code === 'APP_ACCESS_DENIED') {
                                console.warn('🚫 App access denied for this user.');
                                setAccessDenied(true);
                                return null;
                            }
                        }
                        catch { /* ignore parse error */ }
                    }
                    // Non-fatal: local dev may not have the Edge Function running
                    console.warn('⚠️ Supabase session exchange failed (is the Edge Function deployed?):', response.status);
                    return null;
                }
                const { access_token, supabase_user_id } = await response.json();
                if (access_token) {
                    // Store the token — the custom fetch interceptor picks it up on every request
                    _currentAccessToken = access_token;
                    setAccessToken(access_token);
                    // Also update Realtime WebSocket auth
                    supabaseClient.realtime.setAuth(access_token);
                    console.log('✅ Supabase access token stored. All DB requests will now use authenticated role.');
                    return supabase_user_id;
                }
            }
            catch (err) {
                console.warn('⚠️ Firebase→Supabase session exchange error (non-fatal in local dev):', err);
            }
            return null;
        };
        // 4. Handle SSO & redirect logic
        const hydrateAuth = async () => {
            var _a;
            try {
                if (allowLocalBypass) {
                    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                    const isWebContainer = window.location.hostname.includes('webcontainer-api.io') || window.location.hostname.includes('stackblitz.io') || window.location.hostname.includes('bolt.new') || window.location.hostname.includes('.local.webcontainer.io');
                    const isExplicitBypass = ((_a = import.meta.env) === null || _a === void 0 ? void 0 : _a.VITE_ALLOW_DEV_BYPASS) === 'true';
                    if (isLocalhost || isWebContainer || isExplicitBypass) {
                        console.warn('🔐 Local dev mode detected. Bypassing strict SSO redirect.');
                        return;
                    }
                }
                let sharedToken = Cookies.get('shared_sso_token');
                // Fallback: Check URL for token if cross-domain cookie was blocked
                const urlParams = new URLSearchParams(window.location.search);
                const urlToken = urlParams.get('ssoToken');
                if (urlToken) {
                    sharedToken = urlToken;
                    // Clear the ssoToken from the URL and clear the in-flight guard
                    sessionStorage.removeItem('sso_redirect_in_flight');
                    urlParams.delete('ssoToken');
                    const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
                    window.history.replaceState({}, document.title, newUrl);
                }
                if (!auth.currentUser && sharedToken) {
                    try {
                        await signInWithCustomToken(auth, sharedToken);
                        sessionStorage.removeItem('sso_redirect_in_flight');
                        // onAuthStateChanged and onIdTokenChanged will handle the rest
                    }
                    catch (error) {
                        console.error('SSO Login failed:', error);
                        sessionStorage.removeItem('sso_redirect_in_flight');
                        setSsoFailed(true);
                        if (loginRedirectUrl) {
                            window.location.href = `${loginRedirectUrl}?redirect=${encodeURIComponent(window.location.href)}`;
                        }
                    }
                }
                else if (!auth.currentUser && !sharedToken) {
                    await auth.authStateReady();
                    if (!auth.currentUser) {
                        // Guard: only redirect once per tab session to prevent infinite loops
                        // if the SSO token generation fails on the login side.
                        if (sessionStorage.getItem('sso_redirect_in_flight')) {
                            console.warn('⚠️ SSO redirect already attempted this session. Stopping to prevent infinite loop.');
                            setSsoFailed(true);
                            setLoading(false);
                            return;
                        }
                        if (loginRedirectUrl) {
                            sessionStorage.setItem('sso_redirect_in_flight', '1');
                            window.location.href = `${loginRedirectUrl}?redirect=${encodeURIComponent(window.location.href)}`;
                        }
                    }
                }
            }
            finally {
                hydratingRef.current = false;
            }
        };
        hydrateAuth();
        // 5. Listen for auth state changes (sign in / sign out)
        //    Don't set loading=false while hydrateAuth is still running—
        //    this prevents the 'Session Ended' screen from flashing.
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (!currentUser) {
                // User signed out — clear everything
                setUser(null);
                _currentAccessToken = null;
                setAccessToken(null);
                supabaseClient.auth.signOut();
                if (!hydratingRef.current) {
                    setLoading(false);
                }
            }
        });
        // 6. Listen for token refreshes (~every hour).
        //    onIdTokenChanged fires BEFORE onAuthStateChanged, so it handles both
        //    the initial sign-in session creation and ongoing token refreshes.
        const unsubscribeTokenListener = getAuth().onIdTokenChanged(async (firebaseUser) => {
            var _a;
            if (firebaseUser) {
                // Extract allowedApps from custom claims
                try {
                    const tokenResult = await firebaseUser.getIdTokenResult();
                    const apps = tokenResult.claims.allowedApps || [];
                    setAllowedApps(apps);
                }
                catch {
                    // Non-fatal — claims may not exist yet
                }
                const supabaseId = await exchangeForSupabaseSession(firebaseUser);
                // Build the unified AuthUser — user.id is the Supabase UUID
                setUser(supabaseId ? {
                    id: supabaseId,
                    email: (_a = firebaseUser.email) !== null && _a !== void 0 ? _a : '',
                    displayName: firebaseUser.displayName,
                    firebaseId: firebaseUser.uid,
                } : null);
                setLoading(false); // Always unblock the app, even if exchange fails (local dev)
            }
        });
        return () => {
            unsubscribe();
            unsubscribeTokenListener();
        };
    }, [firebaseConfig, supabaseUrl, supabaseAnonKey, loginRedirectUrl, appId]);
    // The Local Dev Bypass Render
    if (allowLocalBypass && !loading && !user) {
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const isWebContainer = window.location.hostname.includes('webcontainer-api.io') || window.location.hostname.includes('stackblitz.io') || window.location.hostname.includes('bolt.new') || window.location.hostname.includes('.local.webcontainer.io');
        const isExplicitBypass = ((_a = import.meta.env) === null || _a === void 0 ? void 0 : _a.VITE_ALLOW_DEV_BYPASS) === 'true';
        if (isLocalhost || isWebContainer || isExplicitBypass) {
            return React.createElement(DevLoginScreen, null);
        }
    }
    const signOut = async () => {
        const auth = getAuth();
        await auth.signOut();
    };
    // Access Denied Render (user is authenticated but not authorized for this app)
    if (!loading && accessDenied) {
        return (React.createElement("div", { style: { display: 'flex', minHeight: '100vh', width: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC', fontFamily: 'system-ui, -apple-system, sans-serif' } },
            React.createElement("div", { style: { backgroundColor: 'white', padding: '2.5rem', borderRadius: '0.75rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', textAlign: 'center', maxWidth: '400px', width: '90%' } },
                React.createElement("div", { style: { width: '4rem', height: '4rem', backgroundColor: '#FEF2F2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' } },
                    React.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "#DC2626", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                        React.createElement("circle", { cx: "12", cy: "12", r: "10" }),
                        React.createElement("line", { x1: "4.93", y1: "4.93", x2: "19.07", y2: "19.07" }))),
                React.createElement("h2", { style: { fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.75rem' } }, "Access Denied"),
                React.createElement("p", { style: { color: '#4b5563', marginBottom: '1.75rem', fontSize: '0.95rem', lineHeight: '1.5' } }, "You don't have permission to access this application. Please contact your administrator to request access."),
                loginRedirectUrl && (React.createElement("button", { onClick: () => { window.location.href = loginRedirectUrl; }, style: { width: '100%', padding: '0.75rem 1rem', backgroundColor: '#1F4E79', color: 'white', borderRadius: '0.5rem', fontWeight: '600', fontSize: '1rem', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s' }, onMouseOver: (e) => { e.currentTarget.style.backgroundColor = '#2a6399'; }, onMouseOut: (e) => { e.currentTarget.style.backgroundColor = '#1F4E79'; } }, "Return to Main App")))));
    }
    // The SSO Redirect Fallback Render (when user is signed out normally)
    if (!loading && !user) {
        if (loginRedirectUrl) {
            return (React.createElement("div", { style: { display: 'flex', minHeight: '100vh', width: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC', fontFamily: 'system-ui, -apple-system, sans-serif' } },
                React.createElement("div", { style: { backgroundColor: 'white', padding: '2.5rem', borderRadius: '0.75rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', textAlign: 'center', maxWidth: '400px', width: '90%' } },
                    React.createElement("div", { style: { width: '4rem', height: '4rem', backgroundColor: '#EFF6FF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' } },
                        React.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "#1F4E79", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                            React.createElement("path", { d: "M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" }),
                            React.createElement("polyline", { points: "10 17 15 12 10 7" }),
                            React.createElement("line", { x1: "15", y1: "12", x2: "3", y2: "12" }))),
                    React.createElement("h2", { style: { fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.75rem' } }, "Session Ended"),
                    React.createElement("p", { style: { color: '#4b5563', marginBottom: '1.75rem', fontSize: '0.95rem', lineHeight: '1.5' } }, "You have been securely signed out. Please sign back in to access your dashboard."),
                    React.createElement("button", { onClick: () => {
                            setIsRedirecting(true);
                            setSsoFailed(false);
                            sessionStorage.removeItem('sso_redirect_in_flight');
                            sessionStorage.setItem('sso_redirect_in_flight', '1');
                            window.location.href = `${loginRedirectUrl}?redirect=${encodeURIComponent(window.location.href)}`;
                        }, disabled: isRedirecting, style: { width: '100%', padding: '0.75rem 1rem', backgroundColor: isRedirecting ? '#9ca3af' : '#1F4E79', color: 'white', borderRadius: '0.5rem', fontWeight: '600', fontSize: '1rem', border: 'none', cursor: isRedirecting ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }, onMouseOver: (e) => { if (!isRedirecting)
                            e.currentTarget.style.backgroundColor = '#2a6399'; }, onMouseOut: (e) => { if (!isRedirecting)
                            e.currentTarget.style.backgroundColor = '#1F4E79'; } }, isRedirecting ? (React.createElement(React.Fragment, null,
                        React.createElement("svg", { style: { animation: 'spin 1s linear infinite' }, width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                            React.createElement("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" })),
                        "Redirecting...")) : ('Sign In via SSO')),
                    ssoFailed && (React.createElement("p", { style: { color: '#9ca3af', fontSize: '0.8rem', marginTop: '1.25rem', lineHeight: '1.4' } },
                        "SSO unavailable?",
                        ' ',
                        React.createElement("button", { onClick: () => {
                                sessionStorage.removeItem('sso_redirect_in_flight');
                                window.location.href = window.location.origin + '?directLogin=1';
                            }, style: { color: '#1F4E79', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 'inherit' } }, "Sign in with email"))),
                    React.createElement("style", null, `@keyframes spin { 100% { transform: rotate(360deg); } }`))));
        }
    }
    return (React.createElement(AuthContext.Provider, { value: { user, loading, supabase, accessToken, allowedApps, signOut } }, !loading && user && children));
};
// Export a simple hook for the bolt apps to consume
export const useAuth = () => useContext(AuthContext);
